import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Users, Edit, Trash2, Plus } from 'lucide-react';
import { getProfiles, deleteProfile } from '../lib/ProfileDataService';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import DataTable from '../components/ui/DataTable';

const Profiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, profileId: null, profileName: '' });
  const [deleting, setDeleting] = useState(false);

  const getSearchTypeLabel = (searchType) => {
    const labels = {
      1: t('contact'),
      2: t('submission'),
      3: t('lead'),
      4: t('opportunity'),
    };
    return labels[searchType] || t('unknown');
  };

  const fetchProfiles = async () => {
    try {
      setError(null);
      const response = await getProfiles(1);
      setProfiles(response.profiles || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching profiles:', err);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);



  const handleRowAction = async (action, row) => {
    switch (action) {
      case 'edit':
        navigate(`/profiles/edit/${row.id}`);
        break;
      case 'delete':
        const profile = profiles.find(p => p.id === row.id);
        setDeleteConfirm({
          isOpen: true,
          profileId: row.id,
          profileName: profile?.name || 'this profile'
        });
        break;
      default:
        console.log('Unknown action:', action, row);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.profileId) return;

    try {
      setDeleting(true);
      await deleteProfile(deleteConfirm.profileId);
      
      // Refresh profiles list
      await fetchProfiles();
      
      // Close modal
      setDeleteConfirm({ isOpen: false, profileId: null, profileName: '' });
    } catch (err) {
      console.error('Error deleting profile:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirm({ isOpen: false, profileId: null, profileName: '' });
  };

  const handleCreateProfile = () => {
    navigate('/profiles/edit/new');
  };

  const columns = [
    {
      key: 'searchType',
      header: t('searchType'),
      render: (searchType) => getSearchTypeLabel(searchType)
    },
    {
      key: 'name',
      header: t('profileName'),
      fontWeight: 'font-medium'
    },
    {
      actions: [
        {
          key: 'edit',
          icon: <Edit className="h-4 w-4" />,
          label: t('edit'),
          title: t('edit'),
          disabled: deleting
        },
        {
          key: 'delete',
          icon: <Trash2 className="h-4 w-4" />,
          label: t('delete'),
          title: t('delete'),
          disabled: deleting
        }
      ]
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 sm:gap-3">
            <Users className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            {t('profiles')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('manageProfiles')}
          </p>
        </div>
        <Button onClick={handleCreateProfile} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          {t('createProfile')}
        </Button>
      </div>

      {/* Profiles Cards */}
      <DataTable
        data={profiles}
        columns={columns}
        error={error ? `${t('errorLoadingProfiles')}: ${error}` : null}
        emptyMessage={t('noProfilesFound')}
        onRowAction={handleRowAction}
        cardView={true}
        cardTitle={(row) => row.name}
        cardSubtitle={(row) => getSearchTypeLabel(row.searchType)}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('deleteProfile')}
        message={t('deleteProfileConfirmation', { profileName: deleteConfirm.profileName })}
        confirmText={deleting ? t('deleting') : t('delete')}
        cancelText={t('cancel')}
        variant="destructive"
        disabled={deleting}
      />
    </div>
  );
};

export default Profiles; 